import { ViewportScroller } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormNotesCustomersComponent } from '../../customers/components/notes-customers-form/notes-customers-form.component';
import { RankingBarComponent } from '../../customers/components/ranking-bar/ranking-bar.component';
import { ProposalsAttachmentsPanelComponent } from '../components/proposals-attachments-panel/proposals-attachments-panel.component';
import { ProposalsCampaignsComponent } from '../components/proposals-campaigns/proposals-campaigns.component';
import { ProposalsDataComponent } from '../components/proposals-data/proposals-data.component';
import { ProposalsFormComponent } from '../components/proposals-form/proposals-form.component';
import { ProposalsItemsComponent } from '../components/proposals-items/proposals-items.component';
import { ProposalsOtherComponent } from '../components/proposals-other/proposals-other.component';
import { ProposalsProgressBarComponent } from '../components/proposals-progress-bar/proposals-progress-bar.component';
import { ActivatedRoute } from '@angular/router';
import { ProposalsDetailsFormRequestComponent } from '../components/proposals-details-form-request/proposals-details-form-request.component';
import { ProposalsService } from '../../../services/api/proposals.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TextAreaFieldComponent } from '../../../components/form/textarea-field/textarea-field.component';
import { HttpParams } from '@angular/common/http';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { LoaderService } from 'src/app/services/loader.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProposalsScopeChangeComponent } from '../components/proposals-scope-change/proposals-scope-change.component';
import { RouteService } from 'src/app/services/route.service';
import { AccessControlService } from 'src/app/services/access-control.service';

@Component({
  selector: 'app-proposals-detail-page',
  imports: [ProposalsProgressBarComponent, RankingBarComponent, ProposalsDetailsFormRequestComponent, ProposalsFormComponent, ProposalsDataComponent, FormNotesCustomersComponent, ProposalsItemsComponent, ProposalsAttachmentsPanelComponent, ProposalsOtherComponent, ProposalsCampaignsComponent, ReactiveFormsModule, TextAreaFieldComponent, ProposalsScopeChangeComponent],
  templateUrl: './proposals-detail-page.component.html',
  styleUrl: './proposals-detail-page.component.scss'
})
export class ProposalsDetailPageComponent implements OnInit, OnDestroy {
  proposal: any;
  proposalItems: any[] = [];
  proposalNotes: any;
  readonly: boolean = true;
  proposalId!: number;
  reasonControl = new FormControl('');
  isScopeChangePage: boolean = false;
  canEditProposal = false;
  canActOnProposal = false;
  private destroy$ = new Subject<void>();
  private readonly LOADER_CONTEXT = 'proposals-detail-page';
  @ViewChild('rejectProposalModal') private rejectProposalModal!: TemplateRef<unknown>;
  private rejectModalRef?: NgbModalRef;

  constructor(
    private route: ActivatedRoute,
    protected proposalsService: ProposalsService,
    private router: Router, 
    private mainLayoutService: MainLayoutService,
    private alertNotificationService: AlertNotificationService,
    private loaderService: LoaderService,
    private modalService: NgbModal,
    private viewportScroller: ViewportScroller,
    private routeService: RouteService,
    private accessControlService: AccessControlService
  ) {
  }

  ngOnInit(): void {
    this.proposalId = Number(this.route?.snapshot?.paramMap?.get('proposalId'));
    if (this.proposalId) {
      this.loadData(this.proposalId);
    }
    
    this.mainLayoutService.isScopeChangePage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.isScopeChangePage = value;
      });
  
    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canEditProposal = this.accessControlService.hasFeatureActionAccess(profileCode, 'PROPOSALS_LIST', 'modify');
        this.canActOnProposal = this.accessControlService.hasFeatureActionAccess(profileCode, 'PROPOSALS_DETAIL_ACTIONS', 'modify');
      });
  }

  loadData(proposalId: any) {
    const params = new HttpParams().set('size', '9999');

    this.proposalsService.detail(proposalId).subscribe((response: any) => {
      this.proposal = response?.data?.proposal;
      
      if (this.proposal && !this.proposal.hasOwnProperty('isShowButton')) {
        this.proposal.isShowButton = true;
      }
      
      this.mainLayoutService.setRelatedCodeItem(this.proposal.code || null);
      this.mainLayoutService.setRelatedClientName(this.proposal.customer?.companyName || null);
      this.mainLayoutService.setRelatedBrandName(this.proposal.request?.customer?.brand || null);
      
      if (this.proposal && this.proposal.isChangeOfScope === true) {
        this.mainLayoutService.setIsScopeChangePage(true);
      } else {
        this.mainLayoutService.setIsScopeChangePage(false);
      }
      
      this.proposalsService.listItems(proposalId, { params }).subscribe((itemResponse: any) => {
        if (itemResponse?.status?.code === 200) {
          this.proposalItems = itemResponse?.data?.proposalItems;
        } else {
          console.error('Errore nel caricamento degli items:', itemResponse);
        }
      });
    });

    this.proposalsService.listNotes(proposalId, { params }).subscribe(response => {
      if (response.status.code === 200) {
        this.proposalNotes = response?.data?.sectionNotes;
      }
    });
  }

  openRejectModal(): void {
    this.reasonControl.reset();
    this.rejectModalRef = this.modalService.open(this.rejectProposalModal, {
      centered: true,
      backdrop: 'static'
    });
    this.rejectModalRef.result.catch(() => {
      this.rejectModalRef = undefined;
    });
  }

  confirmRejection(): void {
    if (!this.proposal) {
      return;
    }

    const reason = this.reasonControl.value?.trim();
    if (!reason) {
      this.alertNotificationService.warning('Please provide a reason for rejection');
      return;
    }

    const scrollPosition = this.viewportScroller.getScrollPosition();

    this.loaderService.startLoading(this.LOADER_CONTEXT);
    const status = 'NOT_ACCEPTED';
    const rejectionData = { notes: reason };
    
    this.proposalsService.changeStatus(this.proposal.proposalId, status, rejectionData).subscribe({
      next: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        
        this.rejectModalRef?.dismiss();
        this.rejectModalRef = undefined;
        
        this.reasonControl.reset();
        
        this.alertNotificationService.success('Proposal rejected successfully');
        

        this.loadData(this.proposalId);
        
        setTimeout(() => {
          this.viewportScroller.scrollToPosition(scrollPosition);
        }, 100);
      },
      error: (err) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        console.error('Error rejecting proposal:', err);
        this.alertNotificationService.error(
          'Error rejecting proposal: ' + (err?.error?.message || err?.message || 'Unknown error')
        );
      },
    });
  }

  acceptProposal(content: any): void {
    if (!this.proposal) {
      return;
    }

    this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true
    }).result.then(
      (result) => {
        if (result === 'confirm') {
          const scrollPosition = this.viewportScroller.getScrollPosition();

          this.loaderService.startLoading(this.LOADER_CONTEXT);
          const status = 'ACCEPTED';
          
          this.proposalsService.changeStatus(this.proposal.proposalId, status).subscribe({
            next: () => {
              this.loaderService.stopLoading(this.LOADER_CONTEXT);
              this.alertNotificationService.success('Proposal accepted successfully');
              
              this.loadData(this.proposalId);
              setTimeout(() => {
                this.viewportScroller.scrollToPosition(scrollPosition);
              }, 100);
            },
            error: (err) => {
              this.loaderService.stopLoading(this.LOADER_CONTEXT);
              console.error('Error accepting proposal:', err);
              this.alertNotificationService.error(
                'Error accepting proposal: ' + (err?.error?.message || err?.message || 'Unknown error')
              );
            }
          });
        }
      },
      () => { }
    );
  }

  reopenProposal(): void {
    if (!this.proposal) {
      return;
    }

    this.loaderService.startLoading(this.LOADER_CONTEXT);
    const status = 'SALES';

    this.proposalsService.changeStatus(this.proposal.proposalId, status).subscribe({
      next: () => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.success('Proposal reopened successfully');
        this.routeService.proposalsUpdate(this.proposal.proposalId);
        this.viewportScroller.scrollToPosition([0, 0]);
      },
      error: (err) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        console.error('Error reopening proposal:', err);
        this.alertNotificationService.error(
          'Error reopening proposal: ' + (err?.error?.message || err?.message || 'Unknown error')
        );
      }
    });
  }

  goToProject(): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);
    this.proposalsService.createProjectFromProposal(this.proposalId, {}).subscribe({
      next: (response: any) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        if (response?.status?.code === 200 || response?.status?.code === 201) {
          const projectId = response?.data?.project?.projectId;
          if (projectId) {
            this.alertNotificationService.success('Project successfully created');
            this.router.navigate([`/app/projects/update/${projectId}`]).then(() => {
              this.viewportScroller.scrollToPosition([0, 0]);
            });
          } else {
            this.alertNotificationService.error('Project created but ID not found');
            this.router.navigate(['/app/projects']);
          }
        } else {
          this.alertNotificationService.error('Error creating project');
        }
      },
      error: (error: any) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        console.error('Error creating project from proposal:', error);
        this.alertNotificationService.error('Error creating project: ' + (error?.error?.message || error?.message || 'Unknown error'));
      }
    });
  }

  goToEditProposal(): void {
    if (!this.proposal) {
      return;
    }
    this.routeService.proposalsUpdate(this.proposal.proposalId);
  }
 
  ngOnDestroy(): void {
    this.mainLayoutService.setRelatedCodeItem(null);
    this.mainLayoutService.setRelatedClientName(null);
    this.mainLayoutService.setRelatedBrandName(null);
    this.mainLayoutService.setIsScopeChangePage(false);
    
    this.destroy$.next();
    this.destroy$.complete();
  }
}
